import isEmpty from 'lodash/isEmpty';

import { FormDocument } from '@/features/ui/DocumentsData/model/types';

import {
  formatNullableDate,
  formatRequiredDate,
  toStringValue,
  toStringValueRequired,
} from '@/shared/lib/helpers/payloadHelpers';
import { SelectOption } from '@/shared/types';

import { ExtractFormType } from '@/modules/iac/pages/services/ExtendedCriminalRecord/lib/constants';
import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { FormDataType } from '@/api/types';

import { NamesBeforeValue, UserPayload } from './generatePayload';

export interface AddressPayload {
  country_id: Nullable<string>;
  country_name: string;
  region_id: Nullable<string>;
  region_name: Nullable<string>;
  district_id: Nullable<string>;
  district_name: Nullable<string>;
  settlement_id: Nullable<string>;
  settlement_name: string;
}

export const buildUser = (formData: FormDataType, passportDocument?: Nullable<FormDocument>) => {
  const passportInfo = (passportDocument?.info ?? {}) as Record<string, unknown>;

  const result: UserPayload = {
    last_name: toStringValueRequired(formData.lastName),
    first_name: toStringValueRequired(formData.firstName),
    patronymic: toStringValue(formData.middleName),
    birthday: formatRequiredDate(formData.dateOfBirth),
    phones: Array.isArray(formData.phones)
      ? formData.phones.filter((phone): phone is string => typeof phone === 'string' && !!phone)
      : [],
    email: toStringValueRequired(formData.email),
    serial: toStringValue(passportInfo.serialNumberDoc),
    issued: formatNullableDate(passportInfo.dateIssue ?? passportInfo.date),
    expired: formatNullableDate(passportInfo.expirationDate),
    auth_dep: toStringValue(passportInfo.auth_dep ?? passportInfo.issue),
    unzr: toStringValue(passportInfo.unzr),
    rnokpp: toStringValueRequired(formData.rnokpp),
    sex:
      typeof formData.sex === 'string'
        ? formData.sex
        : toStringValueRequired((formData.sex as Record<string, unknown> | undefined)?.id),
    citizenship_id: toStringValue((formData.citizenship as Record<string, unknown> | undefined)?.id),
    citizenship_name: toStringValue((formData.citizenship as Record<string, unknown> | undefined)?.label),
  };

  if (!isEmpty(formData.namesBefore)) {
    result.names_before = formData.namesBefore as NamesBeforeValue[];
  }

  if (formData.extractFormType === ExtractFormType.PAPER) {
    result.doc_type = toStringValueRequired(passportDocument?.type?.code);
    result.doc_name = toStringValueRequired(passportDocument?.type?.label);
  }

  return result;
};

export const getRegisteredAddress = (formData: ExtendedCriminalRecordExtractSchema) => {
  return mapAddress({
    country: formData.registeredCountry,
    region: formData.registeredRegion,
    district: formData.registeredDistrict,
    settlement: formData.registeredCity,
  });
};

export const getBirthAddress = (formData: ExtendedCriminalRecordExtractSchema) => {
  return mapAddress({
    country: formData.birthCounttry,
    region: formData.birthRegion,
    district: formData.birthDistrict,
    settlement: formData.birthCity,
  });
};

const toLabel = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) return toStringValueRequired((value as { label?: unknown }).label);
  if (typeof value === 'string') return value;
  return '';
};

const mapAddress = (address?: Record<string, unknown>): AddressPayload => {
  const country = address?.country;
  const region = address?.region;
  const district = address?.district;
  const settlement = address?.settlement;

  return {
    country_id: toStringValue((country as SelectOption)?.id),
    country_name: toLabel(country),
    region_id: toStringValue((region as SelectOption)?.id),
    region_name: toLabel(region),
    district_id: toStringValue((district as SelectOption)?.id),
    district_name: toLabel(district),
    settlement_id: toStringValue((settlement as SelectOption)?.id),
    settlement_name: toLabel(settlement),
  };
};
